/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, computed, inject, ViewChild } from '@angular/core';

import { CalendarComponent } from 'src/app/common/calendar.component';

import { TourismCalendarEventFiltersComponent } from './tourism-calendar-event-filters/tourism-calendar-event-filters.component';
import { deepClone } from '@/_utils/deep-clone';
import { HttpClient } from '@angular/common/http';
import { TourismEventSlots } from '@/bag/bag-transport-term/bag-transport-term.model';
import { TourismEventTransportTermSlotsStore } from '@/tourism-calendar/tourism-calendar-event-view/tourism-event-transport-term-slots-store';

@Component({
	selector: 'app-tourism-calendar',
	templateUrl: './tourism-calendar.component.html',
	styleUrls: ['./tourism-calendar.component.css'],
})
export class TourismCalendarComponent extends CalendarComponent {
	@ViewChild(TourismCalendarEventFiltersComponent)
	TourismCalendarEventFiltersComponent: TourismCalendarEventFiltersComponent;
	showLegend = false;
	data: Array<Vehicle>;
	vehicles: Array<Vehicle> = [];
	drivers = [];
	module = 'tourismCalendar';
	touristGuides = [];
	allVehicles: Array<Vehicle> = [];
	users = [];
	filters = {
		id: null,
		type: null,
	};
	parameters = {
		vehicle: [],
		category: [],
		status: [],
		yesno: [],
		vehicle_type: [],
		vehicle_busType: [],
		vehicle_category: [],
		vehicle_status: [],
		lastLocations: [],
		organization: [],
		profile: [],
		transportTerm: [],
	};
	selectedObject = {
		id: null,
		name: null,
		element: null,
	};
	private readonly transportTermSlotsStore = inject(TourismEventTransportTermSlotsStore);

	private profileLoaded = false;
	private additionalDataLoaded = false;

	protected loadAdditionalData(): void {
		const apiPromises = [
			this.apiGetPromise('/parameters/'),
			this.apiGetPromise('/vehicleBusTypes/'),
			this.apiGetPromise('/vehicleStatuses/'),
			this.apiGetPromise('/drivers/'),
			this.apiGetPromise('/touristGuides/'),
			this.apiGetPromise('/users/'),
			this.apiGetPromise('/tourismEventsStatuses/'),
			this.bagGetPromise('/organization/short_name/?limit=9999'),
		];

		Promise.all(apiPromises).then(
			([
				parameters,
				vehicleBusTypes,
				vehicleStatuses,
				drivers,
				touristGuides,
				users,
				statuses,
				organizations,
			]) => {
				// Process parameters
				this.parameters.vehicle_type = this.api.typeParametersConvert(parameters.data, 1);
				this.parameters.vehicle_category = this.api.typeParametersConvert(parameters.data, 4);
				this.parameters.category = this.api.typeParametersConvert(parameters.data, 6);
				this.parameters.yesno = [
					{ id: 1, name: 'Tak' },
					{ id: 0, name: 'Nie' },
				];

				// Assign other data
				this.parameters.vehicle_busType = vehicleBusTypes.data;
				this.parameters.vehicle_status = vehicleStatuses.data;
				this.drivers = drivers.data;
				this.touristGuides = touristGuides.data;
				this.users = users.data;
				this.parameters.status = statuses.data;
				this.parameters.organization = organizations.results;

				this.additionalDataLoaded = true;
				this.loadData();
			},
		);
	}

	protected loadData(): void {
		if (!this.additionalDataLoaded) {
			return;
		}
		delete this.filters['date_from'];
		delete this.filters['date_to'];
		this.loader.show();
		this.loadVehicles(() => {
			this.loadEvents(() => {
				this.loader.hide();
			});
		});
	}

	private apiGetPromise(url: string): Promise<any> {
		return new Promise((resolve) => {
			this.api.get(url, (result, error) => resolve(result));
		});
	}

	private bagGetPromise(url: string): Promise<any> {
		return new Promise((resolve) => {
			this.bag.get(url, (result, error) => resolve(result));
		});
	}

	private loadProfileIfNeeded(data: any[]): Promise<void> {
		const needsProfile = data.some((event) => event.clientEvent?.isOrganization === '0');

		if (!needsProfile || this.profileLoaded) {
			return Promise.resolve();
		}

		return new Promise((resolve) => {
			this.bag.get('/profile/data/?limit=9999', (result, error) => {
				this.parameters.profile = result.results;
				this.profileLoaded = true;
				resolve();
			});
		});
	}

	private loadVehicles(callback) {
		const parms: string = this.api.getChecked(this.filters, this.parameters);
		this.api.get('/vehicles/?' + parms, (result, error) => {
			const vehicles = [];
			result.data.forEach((obj) => {
				if (obj.seats) {
					obj.name += ' (' + obj.seats;
					if (obj.type.id === 'autokar' || obj.type.id === 'autokar_flix') {
						obj.name += '+2)';
					}
					obj.name += ' ' + obj.registrationNumber;
				}
				if (obj.type.id == 'lead') {
					vehicles.unshift(obj);
				} else {
					vehicles.push(obj);
				}
			});
			this.allVehicles = vehicles;
			this.vehicles = vehicles;
			callback();
		});
	}

	private loadEvents(callback) {
		let parms: string = this.api.getChecked(this.filters, this.parameters);
		parms += '&date_from=' + this.date_from;
		parms += '&date_to=' + this.date_to;
		this.api.get('/tourismEvents/?' + parms, (resultErp, error) => {
			// Run slot population and profile loading in parallel
			const slotsPromise = new Promise<void>((resolve) => {
				this.transportTermSlotsStore
					.populateSlotMapWithMatchesToTourismEvents(this.date_from, this.date_to)
					.subscribe((resultBag) => {
						resultBag.results.forEach((bagEvent) => {
							this.transportTermSlotsStore.slotMap = new Map<string, TourismEventSlots>([
								...this.transportTermSlotsStore.slotMap,
								[bagEvent.eventId, bagEvent.slotsCount],
							]);
						});
						resolve();
					});
			});

			Promise.all([slotsPromise, this.loadProfileIfNeeded(resultErp.data)]).then(() => {
				this.data = this.parseDataToGridFormat(resultErp.data);
				callback();
			});
		});
	}

	private countVehicles(events: { vehicle_id: string }[]): { vehicleId: string; count: number }[] {
		const counts = events.reduce((acc: Record<string, number>, { vehicle_id }) => {
			acc[vehicle_id] = (acc[vehicle_id] || 0) + 1;
			return acc;
		}, {});
		return Object.entries(counts).map(([vehicleId, count]) => ({ vehicleId, count }));
	}

	private addDuplicatedEventToArray(
		event: { vehicles: { vehicle_id: string }[] },
		vehicle: Vehicle,
		arrayToPush: unknown[],
	): void {
		const vehiclesCount = this.countVehicles(event.vehicles);
		const target = vehiclesCount.find((v) => v.vehicleId === vehicle.id);
		if (target && target.count > 1) {
			const duplicates = Array.from({ length: target.count - 1 }, () => ({ ...event }));
			arrayToPush.push(...duplicates);
		}
	}

	private addCountToDuplicates(arr: Event[]): Event[] {
		const counts = new Map<string, number>();
		return arr.map((item) => {
			const count = counts.get(item.id) ?? 0;
			counts.set(item.id, count + 1);
			return { ...item, count };
		});
	}

	private parseDataToGridFormat(data) {
		const vehicles: Vehicle[] = [];

		// Pre-build lookup maps for O(1) access instead of O(n) .find() calls
		const statusMap = new Map(this.parameters.status.map((s) => [s.id, s]));
		const organizationMap = new Map(this.parameters.organization.map((o) => [o.id, o]));
		const profileMap = new Map(this.parameters.profile.map((p) => [p.id, p]));
		const usersMap = new Map(this.users.map((u) => [u.id, u]));

		// Clone data once instead of per-vehicle
		const clonedData = deepClone(data);

		// Pre-index events by vehicle_id for faster filtering
		const eventsByVehicle = new Map<string, any[]>();
		clonedData.forEach((event) => {
			event.vehicles.forEach((v: { vehicle_id: string }) => {
				if (!eventsByVehicle.has(v.vehicle_id)) {
					eventsByVehicle.set(v.vehicle_id, []);
				}
				eventsByVehicle.get(v.vehicle_id).push(event);
			});
		});

		// Process vehicles
		this.vehicles.forEach((vehicle) => {
			const events = (eventsByVehicle.get(vehicle.id) || []).map((event) => ({ ...event }));

			events.forEach((event) => {
				event.vehicle = vehicle.id;
				const status = statusMap.get(event.status);
				event.name += ' ' + (status?.name || '');
				if (event?.clientEvent) event.name += ' Klient:';
				if (event.clientEvent?.isOrganization === '1') {
					const client = organizationMap.get(+event.clientEvent?.client);
					event.name += ` ${client?.shortName},`;
				}
				if (event.clientEvent?.isOrganization === '0') {
					const client = profileMap.get(+event.clientEvent?.client);
					event.name += ` ${client?.firstName} ${client?.lastName},`;
				}
				if (event.owner) {
					const owner = usersMap.get(event.owner[0]);
					const ownerName = owner ? ' ' + owner.lastName + ' ' + owner.firstName : '?';
					event.name += ownerName;
				}
				event.background = this.getBackground(event);
				event.border = parseInt(event.errorsCount) ? '#ff0000' : null;
				this.addDuplicatedEventToArray(event, vehicle, events);
			});

			vehicle.events = this.addCountToDuplicates(events);
			const row: any = vehicle;
			row.details = [
				{ label: 'Typ', value: row.type.name },
				{ label: 'Status', value: row.status.name },
				{
					label: '',
					value: '<a href="/vehicles/' + row.id + '" target="_blank">Pojazd</a>',
				},
				{ label: 'Premium', value: row.premium == '1' ? 'Tak' : 'Nie' },
				{ label: 'Nr rejestracyjny', value: row.registrationNumber },
				{ label: 'Długość', value: row.busType.name },
			];
			vehicles.push(row);
		});
		return vehicles;
	}

	private getLastLocation(deviceId): void {
		return this.parameters.lastLocations.find((obj) => obj.clientObject == deviceId);
	}

	private getBackground(event) {
		switch (event.category) {
			case 'EMPLOYEE':
				return '#797777'; // grey
				break;
			case 'CAR':
				return '#797777'; // grey
				break;
			case 'MARKETING':
				return '#797777'; // grey
				break;
			case 'ANTENNA':
				return '#797777'; // grey
				break;
			case 'OTHER':
				return '#38A439'; // green
				break;
			case 'ENSILAGE':
				return '#745509'; // brown
				break;
			case 'SERVICE':
				return '#DFA50D'; // yellow
				break;
			case 'FOREIGN':
				return '#980D9C'; // pink
				break;
			case 'OSKAR':
				return '#0D259C'; // dark blue
				break;
			case 'RENT':
				return '#4890C8'; // light blue
				break;
			case 'GROUP':
				return '#ed6a24'; // orange
				break;
			case 'FOREIGN_SCANDINAVIA':
				return '#f0ff19'; // yellow
				break;
			case 'FLEET':
				return '#ffbe98'; // peach fuzz
				break;
			case 'EMPTY':
				return '#0ddfce';
				break;
			case 'TECHNICAL_EMPTY':
				return '#BEEBF4'; //mint tulip
				break;
			case 'MALFUNCTION':
				return '#7d2de7';
				break;
			case 'ANTENNA_BUS':
				return '#005429';
				break;
			case 'WASH':
				return '#5474ff';
				break;
			default: // red
				return '#DF1D0D';
				break;
		}
	}

	eventOpen(data) {
		this.selectedObject = data;
	}

	eventClosed(eventID) {
		this.selectedObject = {
			id: null,
			name: null,
			element: null,
		};
	}

	filtersOpen() {
		this.TourismCalendarEventFiltersComponent.filtersOpen();
	}
}

class Vehicle {
	id: string;
	name: string;
	events: Event[];
	background: string;
	checked: boolean;
}

class Event {
	id: string;
	name: string;
	date_from: string;
	date_to: string;
	background: string;
	count?: number;
}
